import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import * as React from "react";
import { Fragment, useState } from "react";
import {
  Button,
  useRefresh,
  useUnselectAll,
  useDataProvider,
} from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import { delay } from "utils/tools";
import TableResultConfirmCommercialDocuments from "./TableResultConfirmCommercialDocuments";
import { findIndex } from "lodash";

interface IBulkActionProps {
  filteredData: any[];
  documentType: string;
}

interface IResults {
  id: number;
  description: string;
  type: string;
  status: string;
  processingStatus: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

const BulkActionApproveButton = ({
  filteredData,
  documentType,
}: IBulkActionProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<IResults[]>([]);

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  const selectedCommercialDocuments = filteredData.map((data) => ({
    id: data.id,
    description: data.items[0].description,
    type: data.invoiceTaggedMbcRef ? "credit_note_tagged" : data.type,
    status: data.status,
    processingStatus: "Not Processed",
  }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      refresh();
    }

    setOpen(false);
  };

  const createUnpostedDocument = (
    selectedId: number,
    type: string,
    index: number,
    retryCount: number = 1
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customCreate(`commercialDocuments/${selectedId}/createOnMbc`, {
          data: {},
        })
        .then(() => {
          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].status = "unposted";

          // credit note document with untagged invoice only have unposted document
          if (type === "credit_note") {
            tempInvoices[index].processingStatus = "Finished";
          }

          setResults([...tempInvoices]);
          resolve(true);
        })
        .catch(({ message }: any) => {
          retryCount++;

          if (retryCount <= 3) {
            // give random delay between 2 - 3 seconds
            const delay = Math.floor(Math.random() * 1000) + 2000;
            setTimeout(() => {
              createUnpostedDocument(selectedId, type, index, retryCount)
                .then(resolve)
                .catch(reject);
            }, delay);

            return;
          }

          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].processingStatus = `Error, ${message}`;

          setResults([...tempInvoices]);
          reject();
        });
    });
  };

  // deferral schedule will be added for invoice that has rateChartId
  const addDeferralSchedule = (
    selectedId: number,
    index: number,
    retryCount: number = 1
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customCreate(
          `commercialDocuments/${selectedId}/addDeferralScheduleOnMbc`,
          {
            data: {},
          }
        )
        .then(() => {
          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].status = "unposted";

          setResults([...tempInvoices]);
          resolve(true);
        })
        .catch(({ message }: any) => {
          retryCount++;

          if (retryCount <= 3) {
            // give random delay between 2 - 3 seconds
            const delay = Math.floor(Math.random() * 1000) + 2000;
            setTimeout(() => {
              addDeferralSchedule(selectedId, index, retryCount)
                .then(resolve)
                .catch(reject);
            }, delay);

            return;
          }

          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].processingStatus = `Error, ${message}`;

          setResults([...tempInvoices]);
          reject();
        });
    });
  };

  const createPostedDocument = (
    selectedId: number,
    index: number,
    retryCount: number = 1
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customCreate(`commercialDocuments/${selectedId}/postOnMbc`, {
          data: {},
        })
        .then(() => {
          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].status = "posted";
          tempInvoices[index].processingStatus = "Finished";

          setResults([...tempInvoices]);
          resolve(true);
        })
        .catch(({ message }: any) => {
          retryCount++;

          if (retryCount <= 3) {
            // give random delay between 2 - 3 seconds
            const delay = Math.floor(Math.random() * 1000) + 2000;
            setTimeout(() => {
              createPostedDocument(selectedId, index, retryCount)
                .then(resolve)
                .catch(reject);
            }, delay);

            return;
          }

          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].processingStatus = `Error, ${message}`;

          setResults([...tempInvoices]);
          reject();
        });
    });
  };

  const handleConfirm = async (selectedCommercialDocuments: IResults[]) => {
    await setProcessing(true);

    for (const selectedCommercialDocument of selectedCommercialDocuments) {
      selectedCommercialDocument.processingStatus = "Loading...";
      setResults([...selectedCommercialDocuments]);

      const index = findIndex(selectedCommercialDocuments, function (o) {
        return o.id === selectedCommercialDocument.id;
      });

      try {
        switch (selectedCommercialDocument.type) {
          case "invoice":
            // if the document status is unposted
            // then skip the creation unposted document,
            // need to update the deferral schedule
            if (selectedCommercialDocument.status === "unposted") {
              // add deferral schedule to invoice
              await addDeferralSchedule(selectedCommercialDocument.id, index);

              // posted document
              await createPostedDocument(selectedCommercialDocument.id, index);
              break;
            }

            // create unposted document
            await createUnpostedDocument(
              selectedCommercialDocument.id,
              selectedCommercialDocument.type,
              index
            );

            // add deferral schedule to invoice
            await addDeferralSchedule(selectedCommercialDocument.id, index);

            // posted document
            await createPostedDocument(selectedCommercialDocument.id, index);

            break;
          case "credit_note_tagged":
            // if the document status is unposted
            // then skip the creation unposted document
            if (selectedCommercialDocument.status === "unposted") {
              await createPostedDocument(selectedCommercialDocument.id, index);
              return;
            }

            // create unposted document
            await createUnpostedDocument(
              selectedCommercialDocument.id,
              selectedCommercialDocument.type,
              index
            );
            await createPostedDocument(selectedCommercialDocument.id, index);

            break;
          case "credit_note":
            // create unposted document
            await createUnpostedDocument(
              selectedCommercialDocument.id,
              selectedCommercialDocument.type,
              index
            );

            break;
        }
      } catch (error) {
        continue;
      }

      await delay(2000);
    }

    await setFinished(true);
    await setProcessing(false);
    await unselectAll("commercialDocuments");
  };

  return (
    <Fragment>
      <Button label="Approve Selected" onClick={handleOpen} />
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Approve Selected {documentType}s
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmCommercialDocuments records={results} />
          ) : (
            <DialogContentText color="initial">
              Are you sure you want to approve this selected{" "}
              {documentType.toLowerCase()}s?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={() => handleConfirm(selectedCommercialDocuments)}
              color="primary"
              label="Confirm"
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default BulkActionApproveButton;
